import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { change, Field, getFormSyncErrors, getFormMeta } from 'redux-form';
import { Icon, Spacing, TagButton, Text } from '@reservamos/elements';
import { fieldRenderer } from 'utils/formRenderers';
import { LAP_CHILD_FIELDS, LAP_CHILD_AGE_EXCEEDED_ERROR, hasLapChildData } from 'utils/lapChild';
import getDocTypeRegex from 'utils/getDocTypeRegex';
import normalizeRegex from 'utils/normalizeRegex';
import normalizeName from '../../../utils/normalizeName';
import BirthDateField from '../../BirthDateField';
import DocumentTypeField from '../../DocumentTypeField';

/**
 * LapChildField component.
 * Render a collapsible section per adult passenger to declare a lap child.
 * Stores values under `${passenger}.meta.lapChild` so they reach the backend
 * as `passenger.meta.lap_child` after `decamelizeKeys`.
 *
 * Auto-expands on mount for the first N passengers when the URL passenger mix
 * carries `child_in_arms-N`, so users who picked lap children at search time
 * see the sub-form already open without needing to click "Incluir".
 *
 * @param {object} props - passenger, passengerFields, index.
 * @param {string} props.passenger - Redux-form field path prefix for the passenger.
 * @param {object} props.passengerFields - The current values for this passenger.
 * @param {number} props.index - Position of this passenger in the form, used to gate auto-expand.
 * @returns {JSX.Element} The lap child add button or expanded form.
 */
const LapChildField = ({ passenger, passengerFields, index }) => {
  const { t } = useTranslation('passengers');
  const dispatch = useDispatch();
  const childInArmsCount = useSelector(
    (state) => state.search.get('passengers')?.toJS()?.child_in_arms || 0,
  );
  const birthDateError = useSelector(
    (state) =>
      getFormSyncErrors('passengers')(state)?.passengers?.[index]?.meta?.lapChild?.birthDate,
  );
  const birthDateTouched = useSelector(
    (state) =>
      getFormMeta('passengers')(state)?.passengers?.[index]?.meta?.lapChild?.birthDate?.touched,
  );

  const lapChild = passengerFields?.meta?.lapChild || {};
  const hasValue = hasLapChildData(lapChild);
  const isExpanded =
    passengerFields?.meta?.lapChildExpanded ?? (hasValue || index < childInArmsCount);
  const setIsExpanded = (value) =>
    dispatch(change('passengers', `${passenger}.meta.lapChildExpanded`, value));

  const handleRemove = () => {
    setIsExpanded(false);
    LAP_CHILD_FIELDS.forEach((field) => {
      dispatch(change('passengers', `${passenger}.meta.lapChild.${field}`, ''));
    });
  };

  if (!isExpanded) {
    return (
      <div className="add-passenger">
        <TagButton onClick={() => setIsExpanded(true)}>{t('lap_child.add_button')}</TagButton>
      </div>
    );
  }

  const docTypeRegex = getDocTypeRegex('passengers', lapChild.documentType || 'CPF');

  return (
    <Spacing vertical size="S">
      <div className="passenger-detail-title">
        <Text weight="bold" size="M">
          {t('lap_child.title')}
        </Text>
        <button type="button" onClick={handleRemove}>
          <Text elementType="span" weight="semibold" size="S" color="error">
            {t('common:buttons.delete')}
          </Text>
          <Icon type="Cross" color="error" size="XS" hasMargin marginSide="left" />
        </button>
      </div>
      <div className="person-passengers-form">
        <Field
          id={`${passenger}.meta.lapChild.firstName`}
          name={`${passenger}.meta.lapChild.firstName`}
          type="text"
          placeholder={t('lap_child.first_name')}
          component={fieldRenderer}
          normalize={normalizeName}
        />
        <Field
          id={`${passenger}.meta.lapChild.lastName`}
          name={`${passenger}.meta.lapChild.lastName`}
          type="text"
          placeholder={t('lap_child.last_name')}
          component={fieldRenderer}
          normalize={normalizeName}
        />
      </div>
      <Spacing size="S" flexGrow isResponsive>
        <DocumentTypeField
          id={`${passenger}.meta.lapChild.documentType`}
          name={`${passenger}.meta.lapChild.documentType`}
          dataValueRef={`${passenger}.meta.lapChild.documentId`}
          cls="form-item-wrap-l"
          passengerNationality={passengerFields.nationality}
          passengerDocumentType={lapChild.documentType}
          documentIdValue={lapChild.documentId}
          target="passengers"
          index={index}
        />
        <div className="form-item-wrap-r">
          <Field
            id={`${passenger}.meta.lapChild.documentId`}
            name={`${passenger}.meta.lapChild.documentId`}
            type="text"
            placeholder={t('document_number')}
            component={fieldRenderer}
            normalize={(input) => normalizeRegex(docTypeRegex, input)}
          />
        </div>
      </Spacing>
      <Field
        id={`${passenger}.meta.lapChild.birthDate`}
        name={`${passenger}.meta.lapChild.birthDate`}
        component={BirthDateField}
      />
      {birthDateError === LAP_CHILD_AGE_EXCEEDED_ERROR && birthDateTouched && (
        <Text color="error" size="S">
          {t('lap_child.age_error')}
        </Text>
      )}
    </Spacing>
  );
};

LapChildField.propTypes = {
  passenger: PropTypes.string.isRequired,
  passengerFields: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
};

export default LapChildField;
